import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Tag, statusTone, Loader, Empty } from "@/components/kit";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  ShieldCheck, ShieldAlert, QrCode, Star, MapPin, Activity, Award, Globe,
  Languages, Eye, CheckCircle2, FileCheck,
} from "lucide-react";

export default function KridaPass() {
  const [pass, setPass] = useState(null);
  const [creds, setCreds] = useState([]);

  const load = () => {
    api.get("/me/krida-pass").then((r) => setPass(r.data)).catch(() => setPass(false));
    api.get("/me/credentials").then((r) => setCreds(r.data.credentials)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const setVisibility = async (v) => {
    await api.put("/me/visibility", { visibility: v });
    setPass((p) => ({ ...p, visibility: v }));
    toast.success(`Visibility set to ${v}`);
  };

  if (pass === null) return <Loader label="Loading Krida Pass" />;
  if (!pass) return <Empty label="Krida Pass available for professional accounts" />;

  return (
    <div className="fade-up">
      <PageHeader overline="Verified sports identity" title="Krida Pass"
        subtitle="Your persistent, verifiable professional identity — extends your CYRA Pass with sport-specific credentials, certifications and level data." testid="krida-pass-page" />

      <div className="grid lg:grid-cols-5 gap-6">
        {/* The pass card */}
        <div className="lg:col-span-2">
          <div className="relative rounded-3xl p-6 bg-gradient-to-br from-krida-card to-krida-surface border border-krida-crimson/30 shadow-[0_0_50px_rgba(30,136,229,0.3)] overflow-hidden" data-testid="krida-pass-card">
            <div className="scan-beam absolute left-0 h-0.5 w-full bg-gradient-to-r from-transparent via-krida-crimson to-transparent" />
            <div className="flex items-center justify-between mb-5">
              <div className="overline text-krida-crimson">CYRA Krida Pass</div>
              <Tag tone={statusTone(pass.verification_status)}>
                {pass.verification_status === "Verified" ? <ShieldCheck className="w-3 h-3" /> : <ShieldAlert className="w-3 h-3" />}
                {pass.verification_status}
              </Tag>
            </div>
            <div className="flex items-center gap-4">
              <img src={pass.photo} alt={pass.name} className="w-20 h-20 rounded-2xl object-cover border border-white/10" />
              <div>
                <div className="font-heading text-2xl font-bold">{pass.name}</div>
                <div className="text-slate-400 text-sm">{pass.roles[0]?.role} · {pass.level}</div>
                <div className="font-mono text-xs text-krida-emerald mt-1">{pass.krida_id}</div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 mt-6 text-center">
              {[["Rating", pass.rating, Star], ["Games", pass.games_officiated, Activity], ["Exp", `${pass.experience_years}y`, Award]].map(([l, v, Ic]) => (
                <div key={l} className="rounded-lg bg-black/30 border border-white/5 py-2.5">
                  <Ic className="w-4 h-4 mx-auto text-slate-500 mb-1" />
                  <div className="font-mono text-sm font-bold">{v}</div>
                  <div className="overline text-[8px] text-slate-500">{l}</div>
                </div>
              ))}
            </div>
            <div className="mt-6 flex items-center justify-between rounded-xl bg-black/40 border border-white/5 p-4">
              <div>
                <div className="text-xs text-slate-400">Scan to verify identity</div>
                <div className="font-mono text-[10px] text-slate-600 mt-1">{pass.qr_payload}</div>
                {pass.marketplace_eligible && (
                  <div className="flex items-center gap-1.5 mt-2 text-krida-emerald text-xs font-semibold"><CheckCircle2 className="w-3.5 h-3.5" /> Marketplace eligible</div>
                )}
              </div>
              <QrCode className="w-16 h-16 text-slate-200" data-testid="krida-pass-qr" />
            </div>
          </div>

          <div className="krida-card p-5 mt-5">
            <div className="flex items-center gap-2 mb-3"><Eye className="w-4 h-4 text-krida-cobalt" /><span className="font-semibold text-sm">Profile visibility</span></div>
            <Select value={pass.visibility} onValueChange={setVisibility}>
              <SelectTrigger data-testid="visibility-select" className="bg-black/40 border-white/10 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-krida-card border-white/10 text-slate-100">
                {["Private", "Organisation", "Marketplace", "Public"].map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500 mt-2">Sensitive personal data stays private regardless of setting.</p>
          </div>
        </div>

        {/* Details */}
        <div className="lg:col-span-3 space-y-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <InfoCard icon={Activity} title="Sports & roles">
              <div className="flex flex-wrap gap-2">{pass.sports.map((s) => <Tag key={s} tone="cobalt">{s}</Tag>)}</div>
              <div className="flex flex-wrap gap-2 mt-2">{pass.roles.map((r, i) => <Tag key={i} tone="crimson">{r.role}</Tag>)}</div>
            </InfoCard>
            <InfoCard icon={MapPin} title="Location & languages">
              <div className="text-sm text-slate-300">{pass.location.city}, {pass.location.state}</div>
              <div className="flex items-center gap-1.5 flex-wrap mt-2 text-xs text-slate-400"><Languages className="w-3.5 h-3.5" /> {pass.languages.join(", ")}</div>
            </InfoCard>
          </div>

          <div className="krida-card p-6">
            <div className="flex items-center gap-2 mb-4"><Award className="w-5 h-5 text-krida-amber" /><h3 className="font-heading text-xl font-bold">Certifications</h3></div>
            <div className="flex flex-wrap gap-2">{pass.certifications.map((c) => <Tag key={c} tone="amber">{c}</Tag>)}</div>
          </div>

          <div className="krida-card p-6">
            <div className="flex items-center gap-2 mb-4"><FileCheck className="w-5 h-5 text-krida-emerald" /><h3 className="font-heading text-xl font-bold">Credentials & verification</h3></div>
            <div className="space-y-3">
              {creds.length === 0 && <Empty label="No credentials uploaded" />}
              {creds.map((c) => (
                <div key={c.id} className="flex items-center justify-between p-3 rounded-xl bg-black/20 border border-white/5" data-testid={`credential-${c.id}`}>
                  <div>
                    <div className="font-medium">{c.type}</div>
                    <div className="text-xs text-slate-500">{c.issuer} · No. {c.number}</div>
                    <div className="text-xs text-slate-500 mt-0.5">Expires {new Date(c.expiry_date).toLocaleDateString()} {c.days_to_expiry != null && c.days_to_expiry < 90 && <span className="text-krida-amber">({c.days_to_expiry < 0 ? "expired" : `${c.days_to_expiry}d left`})</span>}</div>
                  </div>
                  <Tag tone={statusTone(c.status)}>{c.status}</Tag>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoCard({ icon: Icon, title, children }) {
  return (
    <div className="krida-card p-5">
      <div className="flex items-center gap-2 mb-3"><Icon className="w-4 h-4 text-slate-400" /><span className="font-semibold text-sm">{title}</span></div>
      {children}
    </div>
  );
}
